/**
 * useAudioRecording hook
 *
 * Wraps the browser MediaRecorder API for audio recording.
 * Handles iOS Safari (mp4/aac) vs Chrome (webm/opus) format differences.
 * Manages microphone permissions and recording lifecycle.
 */
import { useState, useRef, useCallback, useEffect } from 'react';

export type RecordingState =
  | 'idle'
  | 'requesting_permission'
  | 'recording'
  | 'stopped';

export interface AudioRecordingResult {
  blob: Blob;
  mimeType: string;
  duration: number;
}

export interface UseAudioRecordingReturn {
  /** Current recording state */
  state: RecordingState;
  /** Whether actively recording */
  isRecording: boolean;
  /** Elapsed recording time in seconds */
  duration: number;
  /** Completed audio blob (available after stop) */
  audioBlob: Blob | null;
  /** MIME type of the recorded audio */
  mimeType: string | null;
  /** Start recording — requests mic permission on first call */
  startRecording: () => Promise<void>;
  /** Stop current recording */
  stopRecording: () => void;
  /** Clear the recorded blob to reset for next recording */
  reset: () => void;
  /** Error message if something went wrong */
  error: string | null;
  /** Whether MediaRecorder API is supported */
  isSupported: boolean;
  /** Audio analyser node for waveform visualization */
  analyserNode: AnalyserNode | null;
}

/** Detect the best supported audio MIME type for the current browser */
function getSupportedMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;

  // Prefer webm/opus (Chrome, Firefox, Android)
  // Fall back to mp4/aac (iOS Safari)
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/aac',
  ];

  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }

  // Last resort: let the browser pick
  return '';
}

/** Check if the MediaRecorder API is available */
function checkMediaRecorderSupport(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  );
}

export function useAudioRecording(): UseAudioRecordingReturn {
  const [state, setState] = useState<RecordingState>('idle');
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [mimeType, setMimeType] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const isSupported = checkMediaRecorderSupport();

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError('Audio recording is not supported in this browser.');
      return;
    }

    setError(null);
    setState('requesting_permission');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      // Set up AudioContext + AnalyserNode for waveform visualization
      let analyser: AnalyserNode | null = null;
      try {
        const audioCtx = new AudioContext();
        audioContextRef.current = audioCtx;
        const source = audioCtx.createMediaStreamSource(stream);
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        setAnalyserNode(analyser);
      } catch {
        // Visualization is optional — don't fail recording if AudioContext fails
      }

      const detectedMime = getSupportedMimeType();
      if (detectedMime === null) {
        setError('No supported audio format found.');
        stream.getTracks().forEach((track) => track.stop());
        setState('idle');
        return;
      }

      const recorderOptions: MediaRecorderOptions = {};
      if (detectedMime) {
        recorderOptions.mimeType = detectedMime;
      }

      const mediaRecorder = new MediaRecorder(stream, recorderOptions);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      const actualMime = mediaRecorder.mimeType || detectedMime || 'audio/webm';
      setMimeType(actualMime);

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const recordedBlob = new Blob(chunksRef.current, { type: actualMime });
        const elapsedDuration = (Date.now() - startTimeRef.current) / 1000;
        setAudioBlob(recordedBlob);
        setDuration(elapsedDuration);
        setState('stopped');

        // Clean up stream tracks
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;

        // Clean up audio context
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
        setAnalyserNode(null);
      };

      mediaRecorder.onerror = () => {
        setError('Recording failed unexpectedly.');
        setState('idle');
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };

      // Request data every 250ms for smoother chunk collection
      mediaRecorder.start(250);
      startTimeRef.current = Date.now();
      setAudioBlob(null);
      setDuration(0);
      setState('recording');

      // Tick timer at 100ms for smooth elapsed display
      timerRef.current = window.setInterval(() => {
        setDuration((Date.now() - startTimeRef.current) / 1000);
      }, 100);
    } catch (err: unknown) {
      const domErr = err as DOMException;
      if (domErr.name === 'NotAllowedError' || domErr.name === 'PermissionDeniedError') {
        setError(
          'Microphone access was denied. Please allow microphone access in your browser settings.'
        );
      } else if (domErr.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else {
        setError('Could not access microphone. Please check your browser settings.');
      }
      setState('idle');
    }
  }, [isSupported]);

  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      mediaRecorderRef.current.stop();
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    setAudioBlob(null);
    setMimeType(null);
    setDuration(0);
    setError(null);
    setState('idle');
    chunksRef.current = [];
  }, []);

  return {
    state,
    isRecording: state === 'recording',
    duration,
    audioBlob,
    mimeType,
    startRecording,
    stopRecording,
    reset,
    error,
    isSupported,
    analyserNode,
  };
}
